/**
 * StatsHud — a compact heads-up display for the full-screen canvas.
 * Shows live world stats; numbers use tabular-nums so they don't jitter.
 */

import { motion } from "framer-motion";
import { Activity, DollarSign, Cpu } from "lucide-react";
import { useWorldStats, useRuns } from "../../../api/hooks.js";
import { useWorldStore } from "../../../stores/world.js";
import { ConnectionStatus } from "../../../components/common/ConnectionStatus.js";
import { isActiveRun } from "./runs.js";

function Stat({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2">
      <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}>{icon}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-text-muted">{label}</span>
        <span className="font-mono text-sm tabular-nums text-text-primary">{value}</span>
      </span>
    </div>
  );
}

export function StatsHud() {
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const { data: stats } = useWorldStats();
  const { data: runs } = useRuns(selectedTopologyId ?? undefined);

  const activeCount = (runs ?? []).filter(isActiveRun).length;
  const cost = stats?.totalCostUsd ?? 0;
  const tokens = (stats?.totalTokens.input ?? 0) + (stats?.totalTokens.output ?? 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: "spring", duration: 0.4, bounce: 0 }}
      className="overlay-panel pointer-events-auto flex items-stretch divide-x divide-white/5 rounded-2xl"
    >
      <Stat
        icon={<Activity className="h-3.5 w-3.5 text-brand-green" />}
        label="Active"
        value={String(activeCount)}
        tone="bg-brand-green/10"
      />
      <Stat
        icon={<DollarSign className="h-3.5 w-3.5 text-brand-cyan" />}
        label="Cost"
        value={`$${cost.toFixed(2)}`}
        tone="bg-brand-cyan/10"
      />
      <Stat
        icon={<Cpu className="h-3.5 w-3.5 text-accent-light" />}
        label="Tokens"
        value={tokens.toLocaleString()}
        tone="bg-accent/10"
      />
      <div className="flex items-center gap-2 px-3.5">
        <ConnectionStatus />
      </div>
    </motion.div>
  );
}
